import { type DataProvider } from 'ra-core';
import { ResourcesValue } from '../resources/resources';
import { fetchAllUsers } from './customEndpoints/fetchAllUsers';
import { fetchCurrentUser } from './customEndpoints/fetchCurrentUser';
import { getFeedWidgetsData } from './customEndpoints/fetchFeedWidgetsData';
import { getPostsChartData } from './customEndpoints/fetchPostsChartData';
import { getPulseQuestionsChartData } from './customEndpoints/fetchPulseQuestionsChartData';
import { fetchRequestJourneyReportEmail } from './customEndpoints/fetchRequestJourneyAnalyticsEmail';
import { fetchSendBirdAppId } from './customEndpoints/fetchSendBirdAppId';
import {
  fetchUnitPerformanceData,
  fetchUnitPerformanceInsights,
  fetchUnitPerformanceLlmInsights,
  fetchUnitPerformanceScore,
} from './customEndpoints/fetchUnitPerformanceScores';
import { fetchUsers } from './customEndpoints/fetchUsers';
import { getUsersChartData } from './customEndpoints/fetchUsersChartData';
import { getUsersWidgetsData } from './customEndpoints/fetchUsersWidgetsData';
import { getNudgesReport } from './customEndpoints/getNudgeReport';
import { duplicateNudge } from './customEndpoints/duplicateNudge';
import { getUser } from './customEndpoints/getUser';
import { getUserByInternalId } from './customEndpoints/getUserByInternalId';
import { getUserByPhoneNumber } from './customEndpoints/getUserByPhoneNumber';
import { patchMuteUser } from './customEndpoints/patchMuteUser';
import { patchOperators } from './customEndpoints/patchOperators';
import { postChannelCreate } from './customEndpoints/postChannelCreate';
import { postKickMember } from './customEndpoints/postKickMember';
import { postLeaveChannel } from './customEndpoints/postLeaveChannel';
import { postUser } from './customEndpoints/postUser';
import { resendWelcomeSms } from './customEndpoints/resendWelcomeSms';
import {
  postUnitInsights,
  putUnitInsights,
} from './customEndpoints/setUnitInsights';
import { setUserEnabled } from './customEndpoints/setUserEnabled';
import { setUsersEnabled } from './customEndpoints/setUsersEnabled';
import {
  addKpiAssignees,
  removeKpiAssignees,
} from './customEndpoints/updateKpiAssignees';
import {
  addRoleUsers,
  removeRoleUsers,
} from './customEndpoints/updateRoleUsers';
import {
  addUserGroupMembers,
  removeUserGroupMembers,
} from './customEndpoints/updateUserGroupMembers';
import { uploadFile } from './customEndpoints/uploadFile';
import { uploadUsers } from './customEndpoints/uploadUsers';
import { uploadWebsite } from './customEndpoints/uploadWebsite';
import { getBadgesAwardsChartData } from './hooks/useBadgesAwardsChartData';
import { fetchAllTopics } from './hooks/useFetchAllTopics';
import { fetchSelectList } from './hooks/useFetchSelectList';
import { postInviteToChannel } from './customEndpoints/postInviteToChannel';
import { fetchRequestNudgesReportEmail } from './customEndpoints/fetchRequestNudgesReport';
import { postLlmInsightsForUnit } from './customEndpoints/postLlmInsightsForUnit';
import { type HttpClient } from './useHttpClient';
import { getUnitInsightLlmChat } from './customEndpoints/getUnitInsightLlmChat';
import { getUnitInsightLlmChats } from './customEndpoints/getUnitInsightLlmChats';
import { postUnitInsightLlmChatMessage } from './customEndpoints/postUnitInsightLlmChatMessage';
import { postUnitInsightLlmExperimentalChatMessage } from './customEndpoints/postUnitInsightLlmExperimentalChatMessage';
import { remindNudge } from './customEndpoints/remindNudge';

export type AdminDataProvider = ReturnType<typeof createAdminDataProvider>;

const resourcePaths: { [key in ResourcesValue]?: string } = {
  [ResourcesValue.POSTS]: 'posts',
};

const getResourcePath = (resource: ResourcesValue) => {
  if (resourcePaths[resource]) {
    return `/${resourcePaths[resource]}`;
  } else {
    return `/admin/${resource}`;
  }
};

export const createAdminDataProvider = (httpClient: HttpClient) =>
  ({
    getList: async (resource, params) => {
      const { page, perPage } = params.pagination!;
      const { field, order } = params.sort!;
      const query = new URLSearchParams({
        sort: JSON.stringify([field, order]),
        range: JSON.stringify([(page - 1) * perPage, page * perPage - 1]),
        filter: JSON.stringify(params.filter),
      });

      const path = `${getResourcePath(resource as ResourcesValue)}?${query}`;

      const response = await httpClient.api(path);

      return {
        data: response.json.data,
        total: response.json.total,
      };
    },
    getOne: async (resource, params) => {
      const path = `/admin/${resource}/${params.id}`;

      const response = await httpClient.api(path);

      return {
        data: response.json,
      };
    },
    getMany: async (resource, params) => {
      const idField = params.meta?.idField ?? 'id';

      const query = new URLSearchParams({
        filter: JSON.stringify({ [idField]: params.ids }),
      });

      const path = `/admin/${resource}?${query}`;

      const response = await httpClient.api(path);

      return {
        data: response.json.data.map((d: any) => ({
          id: JSON.stringify(d),
          ...d,
        })),
      };
    },
    // TODO: review this
    getManyReference: async (resource, params) => {
      const { page, perPage } = params.pagination;
      const { field, order } = params.sort;
      const query = new URLSearchParams({
        sort: JSON.stringify([field, order]),
        range: JSON.stringify([(page - 1) * perPage, page * perPage - 1]),
        filter: JSON.stringify({
          ...params.filter,
          [params.target]: params.id,
        }),
      });

      const path = `/admin/${resource}?${query}`;

      const response = await httpClient.api(path);

      return {
        data: response.json.data,
        total: response.json,
      };
    },
    update: async (resource, params) => {
      const path = `/admin/${resource}/${params.id}`;

      const response = await httpClient.api(path, {
        method: 'PUT',
        body: JSON.stringify(params.data),
      });

      return {
        data: response.json,
      };
    },
    updateMany: async (resource, params) => {
      const query = new URLSearchParams({
        filter: JSON.stringify({ id: params.ids }),
      });

      const path = `/admin/${resource}?${query}`;

      const response = await httpClient.api(path, {
        method: 'PUT',
        body: JSON.stringify(params.data),
      });

      return {
        data: response.json,
      };
    },
    create: async (resource, params) => {
      const path = getResourcePath(resource as ResourcesValue);

      const response = await httpClient.api(path, {
        method: 'POST',
        body: JSON.stringify(params.data),
      });

      return {
        data: response.json,
      };
    },
    delete: async (resource, params) => {
      const path = `/admin/${resource}/${params.id}`;

      const response = await httpClient.api(path, {
        method: 'DELETE',
      });

      return {
        data: response.json,
      };
    },
    deleteMany: async (resource, params) => {
      const query = new URLSearchParams({
        filter: JSON.stringify({ id: params.ids }),
      });

      const path = `/admin/${resource}?${query}`;

      await httpClient.api(path, {
        method: 'DELETE',
      });

      return {
        data: params.ids,
      };
    },
    uploadUsers: uploadUsers(httpClient),
    postUser: postUser(httpClient),
    getUserByPhoneNumber: getUserByPhoneNumber(httpClient),
    getUserByInternalId: getUserByInternalId(httpClient),
    getUser: getUser(httpClient),
    setUsersEnabled: setUsersEnabled(httpClient),
    setUserEnabled: setUserEnabled(httpClient),
    resendWelcomeSms: resendWelcomeSms(httpClient),
    fetchCurrentUser: fetchCurrentUser(httpClient),
    fetchUsers: fetchUsers(httpClient),
    fetchAllUsers: fetchAllUsers(httpClient),
    addRoleUsers: addRoleUsers(httpClient),
    removeRoleUsers: removeRoleUsers(httpClient),
    fetchSendBirdAppId: fetchSendBirdAppId(httpClient),
    uploadFile: uploadFile(httpClient),
    uploadWebsite: uploadWebsite(httpClient),
    getPostsChartData: getPostsChartData(httpClient),
    getFeedWidgetsData: getFeedWidgetsData(httpClient),
    getUsersChartData: getUsersChartData(httpClient),
    getUsersWidgetsData: getUsersWidgetsData(httpClient),
    getPulseQuestionsChartData: getPulseQuestionsChartData(httpClient),
    getBadgesAwardsChartData: getBadgesAwardsChartData(httpClient),
    fetchSelectList: fetchSelectList(httpClient),
    fetchAllTopics: fetchAllTopics(httpClient),
    addUserGroupMembers: addUserGroupMembers(httpClient),
    removeUserGroupMembers: removeUserGroupMembers(httpClient),
    addKpiAssignees: addKpiAssignees(httpClient),
    removeKpiAssignees: removeKpiAssignees(httpClient),
    getNudgesReport: getNudgesReport(httpClient),
    fetchUnitPerformanceScore: fetchUnitPerformanceScore(httpClient),
    fetchUnitPerformanceData: fetchUnitPerformanceData(httpClient),
    fetchUnitPerformanceInsights: fetchUnitPerformanceInsights(httpClient),
    fetchUnitPerformanceLlmInsights:
      fetchUnitPerformanceLlmInsights(httpClient),
    postUnitInsights: postUnitInsights(httpClient),
    putUnitInsights: putUnitInsights(httpClient),
    requestJourneyReportEmail: fetchRequestJourneyReportEmail(httpClient),
    requestNudgesReportEmail: fetchRequestNudgesReportEmail(httpClient),
    duplicateNudge: duplicateNudge(httpClient),
    remindNudge: remindNudge(httpClient),
    patchMuteUser: patchMuteUser(httpClient),
    postKickMember: postKickMember(httpClient),
    postChannelCreate: postChannelCreate(httpClient),
    postLeaveChannel: postLeaveChannel(httpClient),
    patchOperators: patchOperators(httpClient),
    postInviteToChannel: postInviteToChannel(httpClient),
    postLlmInsightsForUnit: postLlmInsightsForUnit(httpClient),
    postUnitInsightLlmChatMessage: postUnitInsightLlmChatMessage(httpClient),
    postUnitInsightLlmExperimentalChatMessage:
      postUnitInsightLlmExperimentalChatMessage(httpClient),
    getUnitInsightLlmChat: getUnitInsightLlmChat(httpClient),
    getUnitInsightLlmChats: getUnitInsightLlmChats(httpClient),
  }) satisfies DataProvider;
